// components/ContactFooter.tsx — SkinSio Clinics
"use client";

import { Phone, Mail, Clock, MapPin, Sparkles } from "lucide-react";

export function ContactFooter() {
  return (
    <section
      className="aesthetic-gradient relative py-12 lg:py-16"
      aria-labelledby="contact-footer-title"
    >
      {/* Blend the top edge of this section into whatever sits above it */}
      <div className="section-blend-top" />

      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="mb-12 text-center">
          <div className="bg-rose/10 text-rose mb-2 inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-semibold">
            <Sparkles className="h-4 w-4" aria-hidden="true" />
            Get in Touch
          </div>
          <h2
            id="contact-footer-title"
            className="mb-4 text-3xl font-bold md:text-4xl"
          >
            Visit Us in Vadodara
          </h2>
          <p className="text-muted-foreground mx-auto max-w-lg text-lg">
            Visit our clinic or reach out to us for any inquiries.
          </p>
        </div>

        <div className="grid items-stretch gap-8 md:grid-cols-2">
          {/* Google Map */}
          <div className="relative h-full min-h-[400px] overflow-hidden rounded-2xl shadow-lg ring-1 ring-rose/15">
            <iframe
              src="https://maps.google.com/maps?q=SkinSio%20Vadodara%20A/203%20The%20Atlantic%20Makarpura%20Road%20Vadodara&t=m&z=17&ie=UTF8&iwloc=&output=embed"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="SkinSio Vadodara Clinic Location Map"
            />
          </div>

          {/* Contact Info */}
          <div className="flex flex-col gap-6">
            {/* Address — rose accent */}
            <div className="bg-background border-rose/20 hover-lift group flex flex-col items-center rounded-2xl border p-6 text-center shadow-sm">
              <div className="bg-rose/10 text-rose group-hover:bg-rose mb-4 rounded-2xl p-4 transition-colors duration-300 group-hover:text-white">
                <MapPin className="h-6 w-6" aria-hidden="true" />
              </div>
              <h3 className="mb-2 text-lg font-bold">Visit Us</h3>
              <p className="text-muted-foreground group-hover:text-rose max-w-md font-medium transition-colors">
                2nd Floor, The Atlantic, A/203, Makarpura Rd, beside Indian Oil
                Petrol Pump, near D-Mart, Manjalpur, Vadodara, Gujarat 390010
              </p>
            </div>

            {/* Phone (gold) + Email (navy) */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <a
                href="tel:+919322022392"
                className="bg-background border-golden/30 hover-lift group flex cursor-pointer flex-col items-center rounded-2xl border p-6 text-center shadow-sm"
                aria-label="Call SkinSio Vadodara"
              >
                <div className="bg-golden/10 text-golden group-hover:bg-golden mb-4 rounded-2xl p-4 transition-colors duration-300 group-hover:text-white">
                  <Phone className="h-6 w-6" aria-hidden="true" />
                </div>
                <h3 className="mb-2 text-lg font-bold">Call Us</h3>
                <span className="text-muted-foreground group-hover:text-golden font-medium transition-colors">
                  +91 9322022392
                </span>
              </a>

              <a
                href="mailto:care@skinsioclinics.com"
                className="bg-background border-primary/20 hover-lift group flex cursor-pointer flex-col items-center rounded-2xl border p-6 text-center shadow-sm"
                aria-label="Email SkinSio Vadodara"
              >
                <div className="bg-primary/10 text-primary group-hover:bg-primary mb-4 rounded-2xl p-4 transition-colors duration-300 group-hover:text-white">
                  <Mail className="h-6 w-6" aria-hidden="true" />
                </div>
                <h3 className="mb-2 text-lg font-bold">Email Us</h3>
                <span className="text-muted-foreground group-hover:text-primary font-medium break-all transition-colors">
                  care@skinsioclinics.com
                </span>
              </a>
            </div>

            {/* Working Hours — rose accent */}
            <div className="bg-background border-rose/20 hover-lift group relative overflow-hidden rounded-2xl border p-8 shadow-sm">
              <div className="mb-6 flex items-center justify-center gap-4 md:justify-start">
                <div className="bg-rose/10 text-rose rounded-xl p-3">
                  <Clock className="h-6 w-6" aria-hidden="true" />
                </div>
                <h3 className="text-xl font-bold">Working Hours</h3>
              </div>
              <div className="space-y-4">
                <div className="bg-rose/5 border-rose/10 flex flex-col items-center justify-between rounded-xl border p-3 lg:flex-row">
                  <span className="text-muted-foreground font-medium">Monday - Saturday</span>
                  <span className="text-rose font-bold">10:30 AM - 07:00 PM</span>
                </div>
                <div className="bg-golden/5 border-golden/10 flex flex-col items-center justify-between rounded-xl border p-3 lg:flex-row">
                  <span className="text-muted-foreground font-medium">Sunday</span>
                  <span className="text-golden font-bold">10:30 AM - 03:00 PM</span>
                </div>
                <div className="text-rose flex items-center justify-center gap-2 pt-2 text-sm font-semibold italic">
                  <div className="bg-rose h-2 w-2 animate-pulse rounded-full" aria-hidden="true" />
                  Appointments Required*
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div className="grid grid-cols-2 gap-4">
              <a
                href="https://www.instagram.com/skinsioclinics/"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-background border-border hover-lift group flex flex-col items-center rounded-2xl border p-5 text-center shadow-sm transition-all hover:border-pink-400"
              >
                <div className="mb-3 rounded-2xl bg-gradient-to-br from-[#f09433] via-[#e6683c] to-[#bc1888] p-3 text-white">
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </div>
                <h3 className="mb-1 font-bold">Instagram</h3>
                <span className="text-muted-foreground text-sm">@skinsioclinics</span>
              </a>

              <a
                href="https://www.facebook.com/profile.php?id=61555681362122"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-background border-border hover-lift group flex flex-col items-center rounded-2xl border p-5 text-center shadow-sm transition-all hover:border-blue-400"
              >
                <div className="mb-3 rounded-2xl bg-[#1877F2] p-3 text-white">
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </div>
                <h3 className="mb-1 font-bold">Facebook</h3>
                <span className="text-muted-foreground text-sm">SkinSio Clinics</span>
              </a>

              <a
                href="https://wa.me/919322022392"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-background border-border hover-lift group flex flex-col items-center rounded-2xl border p-5 text-center shadow-sm transition-all hover:border-green-400"
              >
                <div className="mb-3 rounded-2xl bg-[#25D366] p-3 text-white">
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                </div>
                <h3 className="mb-1 font-bold">WhatsApp</h3>
                <span className="text-muted-foreground text-sm">Chat with us</span>
              </a>

              <a
                href="https://www.google.com/maps/search/?api=1&query=SkinSio+Vadodara+2nd+Floor,+The+Atlantic,+A/203,+Makarpura+Rd,+beside+Indian+Oil+Petrol+Pump,+near+D-Mart,+Manjalpur,+Vadodara,+Gujarat+390010"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-background border-border hover-lift group flex flex-col items-center rounded-2xl border p-5 text-center shadow-sm transition-all hover:border-blue-300"
              >
                <div className="mb-3 rounded-2xl bg-[#4285F4] p-3 text-white">
                  <MapPin className="h-5 w-5" aria-hidden="true" />
                </div>
                <h3 className="mb-1 font-bold">Google Maps</h3>
                <span className="text-muted-foreground text-sm">Get directions</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
